// src/components/layout/Footer.tsx - Premium Modern Creative Footer

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Send,
  Heart,
  Sparkles,
  Trophy,
  Users,
  Newspaper,
  Shield,
  Target,
  Calendar,
  Award,
  Globe,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import TikTokIcon from "@/components/icons/TikTokIcon";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const currentYear = new Date().getFullYear();

  const navLabel = (ar: string, en: string) => (isRTL ? ar : en);

  // Footer sections with icons
  const footerSections = [
    {
      title: { ar: "عن الأكاديمية", en: "About Academy" },
      links: [
        { key: "about", path: "/about", label: { ar: "من نحن", en: "About" }, icon: Shield },
        { key: "achievements", path: "/achievements", label: { ar: "الإنجازات", en: "Achievements" }, icon: Trophy },
        { key: "successStories", path: "/success-stories", label: { ar: "قصص النجاح", en: "Success Stories" }, icon: Award },
        { key: "team", path: "/team", label: { ar: "فريق العمل", en: "Team" }, icon: Users },
        { key: "partners", path: "/partners", label: { ar: "الشركاء", en: "Partners" }, icon: Target },
      ],
    },
    {
      title: { ar: "الخدمات", en: "Services" },
      links: [
        { key: "sports", path: "/sports", label: { ar: "الرياضات", en: "Sports" }, icon: Trophy },
        { key: "branches", path: "/branches", label: { ar: "الفروع", en: "Branches" }, icon: MapPin },
        { key: "activities", path: "/activities", label: { ar: "الأنشطة والرحلات", en: "Activities" }, icon: Calendar },
        { key: "booking", path: "/booking", label: { ar: "احجز الآن", en: "Book Now" }, icon: Sparkles },
      ],
    },
    {
      title: { ar: "المعلومات", en: "Information" },
      links: [
        { key: "news", path: "/news", label: { ar: "الأخبار", en: "News" }, icon: Newspaper },
        { key: "press", path: "/press", label: { ar: "الصحافة", en: "Press" }, icon: Globe },
        { key: "testimonials", path: "/testimonials", label: { ar: "آراء العملاء", en: "Testimonials" }, icon: Heart },
        { key: "contact", path: "/contact", label: { ar: "تواصل معنا", en: "Contact" }, icon: Mail },
      ],
    },
    {
      title: { ar: "المجتمع", en: "Community" },
      links: [
        { key: "humanitarian", path: "/humanitarian", label: { ar: "الدور الإنساني", en: "Humanitarian" }, icon: Heart },
        { key: "warImpact", path: "/war-impact", label: { ar: "تأثير الحرب", en: "War Impact" }, icon: Shield },
      ],
    },
  ];

  // Social links with colors
  const socialLinks = [
    { 
      key: "facebook", 
      href: "#", 
      label: { ar: "فيسبوك", en: "Facebook" }, 
      Icon: Facebook, 
      color: "#1877F2" 
    },
    { 
      key: "instagram", 
      href: "#", 
      label: { ar: "إنستغرام", en: "Instagram" }, 
      Icon: Instagram, 
      color: "#E4405F" 
    },
    { 
      key: "youtube", 
      href: "#", 
      label: { ar: "يوتيوب", en: "YouTube" }, 
      Icon: Youtube, 
      color: "#FF0000" 
    },
    { 
      key: "x", 
      href: "#", 
      label: { ar: "إكس", en: "X" }, 
      Icon: Twitter, 
      color: "#000000" 
    },
    {
      key: "linkedin",
      href: "#",
      label: { ar: "لينكدإن", en: "LinkedIn" },
      Icon: Linkedin,
      color: "#0A66C2"
    },
    {
      key: "tiktok",
      href: "#",
      label: { ar: "تيك توك", en: "TikTok" },
      Icon: TikTokIcon,
      color: "#000000"
    },
    {
      key: "whatsapp", 
      href: "#", 
      label: { ar: "واتساب", en: "WhatsApp" }, 
      Icon: MessageCircle, 
      color: "#25D366" 
    },
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: { ar: "البريد الإلكتروني", en: "Email" },
      value: "exapmple@example.com",
      href: "#",
    },
    {
      icon: Phone,
      label: { ar: "الهاتف", en: "Phone" },
      value: "+9xx x2x xx3 xxx",
      href: "#",
    },
    {
      icon: MessageCircle,
      label: { ar: "واتساب", en: "WhatsApp" },
      value: navLabel("تواصل واتساب", "WhatsApp Us"),
      href: "#",
    },
    {
      icon: MapPin,
      label: { ar: "الموقع", en: "Location" },
      value: navLabel("غزة، فلسطين", "Gaza, Palestine"),
      href: "#",
    },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-surface-0 to-surface-1 border-t border-border/40 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{
            y: [0, 50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-champions-accent/5 rounded-full blur-3xl"
          animate={{
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-4 space-y-6"
          >
            <Link to="/" className="inline-block group">
              <motion.div
                className="flex items-center gap-3"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="relative">
                  <img src={logo} alt="Champions Academy" className="h-16 w-auto" />
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-primary/30 to-champions-accent/30 rounded-full blur-2xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-xl font-bold gradient-text">
                    {navLabel("أكاديمية تشامبيونز", "Champions Academy")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {navLabel("جزء من مجموعة تشامبيونز", "Part of Champions Group")}
                  </span>
                </div>
              </motion.div>
            </Link>

            <p className="text-muted-foreground text-sm leading-relaxed">
              {navLabel(
                "نبني أبطال المستقبل من خلال برامج رياضية متكاملة تجمع بين التميز الرياضي والتطوير الشخصي",
                "Building future champions through comprehensive sports programs that combine athletic excellence and personal development"
              )}
            </p>

            {/* Newsletter */}
            {/* <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                {navLabel("اشترك في النشرة الإخبارية", "Subscribe to Newsletter")}
              </h4>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={navLabel("بريدك الإلكتروني", "Your email")}
                  className="flex-1 bg-surface-2 border-border/40"
                />
                <Button size="icon" className="btn-glow shrink-0">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div> */}

            {/* Champions Group Badge */}
            {/* <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-champions-accent/10 border border-primary/20"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Heart className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                {navLabel("عضو فخور في مجموعة تشامبيونز", "Proud Member of Champions Group")}
              </span>
            </motion.div> */}
          </motion.div>

          {/* Links Sections */}
          <div className="lg:col-span-8">
            {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {footerSections.map((section, sectionIdx) => (
                <motion.div
                  key={sectionIdx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: sectionIdx * 0.1 }}
                  className="space-y-4"
                >
                  <h4 className="text-sm font-bold text-foreground relative inline-block">
                    {navLabel(section.title.ar, section.title.en)}
                    <div className="absolute -bottom-1 left-0 w-8 h-0.5 bg-gradient-to-r from-primary to-champions-accent rounded-full" />
                  </h4>

                  <ul className="space-y-3">
                    {section.links.map((link) => {
                      const Icon = link.icon;
                      return (
                        <li key={link.key}>
                          <Link
                            to={link.path}
                            className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-all duration-300"
                          >
                            <Icon className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all duration-300" />
                            <span>{navLabel(link.label.ar, link.label.en)}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </motion.div>
              ))}
            </div> */}
          </div>
        </div>

        {/* Contact & Social Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 py-12 border-y border-border/40"
        > */}
          {/* Contact Info */}
          {/* <div className="space-y-4">
            <h4 className="text-sm font-bold text-foreground flex items-center gap-2 mb-6">
              <Mail className="h-4 w-4 text-primary" />
              {navLabel("تواصل معنا", "Contact Us")}
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.a
                    key={idx}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noreferrer" : undefined}
                    className="group flex items-start gap-3 p-4 rounded-xl bg-surface-2/50 border border-border/40 hover:border-primary/40 hover:bg-surface-2 transition-all duration-300"
                    whileHover={{ y: -2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-1">
                        {navLabel(item.label.ar, item.label.en)}
                      </p>
                      <p className="text-sm font-medium text-foreground truncate">
                        {item.value}
                      </p>
                    </div>
                  </motion.a>
                );
              })}
            </div>
          </div> */}

          {/* Social Links */}
          {/* <div className="space-y-4">
            <h4 className="text-sm font-bold text-foreground flex items-center gap-2 mb-6">
              <Globe className="h-4 w-4 text-primary" />
              {navLabel("تابعنا على وسائل التواصل", "Follow Us on Social Media")}
            </h4>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {socialLinks.map((social, idx) => {
                const Icon = social.Icon;
                return (
                  <motion.a
                    key={social.key}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative aspect-square rounded-xl bg-surface-2/50 border border-border/40 hover:border-primary/40 flex items-center justify-center overflow-hidden"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    whileHover={{ y: -4, scale: 1.05 }}
                    aria-label={navLabel(social.label.ar, social.label.en)}
                  >
                    <motion.div
                      className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity"
                      style={{ backgroundColor: social.color }}
                    />
                    <Icon className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors relative z-10" />
                  </motion.a>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              {navLabel(
                "انضم إلى مجتمعنا المتنامي من الرياضيين والمشجعين",
                "Join our growing community of athletes and supporters"
              )}
            </p>
          </div> */}
        {/* </motion.div> */}

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="pt-8 flex items-center justify-center"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <p className="text-sm text-muted-foreground text-center">
            © {currentYear} Champions Academy.{" "}
            {navLabel("جميع الحقوق محفوظة", "All rights reserved")}.
          </p>
        </motion.div>

        {/* Made with love badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8 text-center"
        >
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-2">
            {navLabel("صُنع بكل", "Made with")}
            <motion.span
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Heart className="h-3 w-3 text-champions-accent fill-champions-accent" />
            </motion.span>
            {navLabel("في غزة، فلسطين", "in Gaza, Palestine")}
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
